import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import { WebPageSchema } from '@/components/feature/SchemaOrg';
import { categories, categoryMap } from '@/mocks/categories';
import { getComicsByCategory, Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/article/${comic.slug}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[3/4] overflow-hidden relative">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 免费韩漫在线漫画`}
          title={`${comic.title} - 无删减下拉式韩漫免费阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          width={600}
          height={800}
        />
        {comic.status === '连载中' && (
          <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
            连载中
          </span>
        )}
        {comic.status === '已完结' && (
          <span className="absolute top-2 left-2 bg-secondary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
            已完结
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
          <div className="flex items-center gap-1 text-xs text-white/90">
            <i className="ri-star-fill text-amber-400"></i>
            <span>{comic.rating}</span>
            <span className="text-white/60">({(comic.ratingCount / 10000).toFixed(1)}万)</span>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{comic.summary}</p>
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {comic.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[11px] text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'love');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const catName = categoryMap[activeCategory] || activeCategory;
  const comicList = getComicsByCategory(activeCategory);
  const breadcrumbItems = getCategoryBreadcrumb(activeCategory);

  const seoTitle = `${catName}韩漫_免费${catName}类在线漫画_无删减下拉式韩漫免费阅读-韩漫漫画网站`;
  const seoDesc = `韩漫漫画网站${catName}分类为您精选优质免费${catName}类韩漫，支持无删减下拉式韩漫免费阅读。收录${comicList.length}部${catName}题材漫画，资源更新及时，推荐精准。`;
  const seoKeywords = `${catName}韩漫,免费${catName}漫画,${catName}在线漫画,无删减下拉式,韩漫免费阅读`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`/category/${activeCategory}`}
      />
      <WebPageSchema
        title={seoTitle}
        description={seoDesc}
        url={`/category/${activeCategory}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Category Header */}
            <div className="py-8">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950">
                {catName}
                <span className="text-base font-normal text-foreground-500 ml-3">
                  共 {comicList.length} 部作品
                </span>
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                精选优质免费{catName}类韩漫，支持无删减下拉式免费阅读
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                  <span className="text-xs opacity-70">({cat.count})</span>
                </Link>
              ))}
            </div>

            {/* Comic Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 pb-16">
              {comicList.map((comic) => (
                <ComicCard key={comic.slug} comic={comic} />
              ))}
            </div>

            {comicList.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类下暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}